import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostStage,
  deleteIndustrialCostStage,
  getIndustrialCostStages,
  updateIndustrialCostStage,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type { CreateStagePayload, Stage, StageType } from '../types/industrialCost';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

const initialForm: CreateStagePayload = {
  name: '',
  code: '',
  stage_type: 'primary',
  display_order: 1,
  is_active: true,
  description: '',
};

function buildStagePayload(stage: Stage): CreateStagePayload {
  return {
    name: stage.name,
    code: stage.code,
    stage_type: stage.stage_type,
    display_order: stage.display_order,
    is_active: stage.is_active,
    description: stage.description || '',
  };
}

function sortStages(items: Stage[]): Stage[] {
  return [...items].sort((a, b) => {
    if (a.display_order !== b.display_order) {
      return a.display_order - b.display_order;
    }
    return a.name.localeCompare(b.name);
  });
}

export default function IndustrialCostStagesView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateStagePayload>(initialForm);

  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [editForm, setEditForm] = useState<CreateStagePayload>(initialForm);

  const [deletingStage, setDeletingStage] = useState<Stage | null>(null);

  const {
    data: stages = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['industrial-cost-stages'],
    queryFn: getIndustrialCostStages,
  });

  const createMutation = useMutation({
    mutationFn: createIndustrialCostStage,
    onSuccess: async () => {
      enqueueSnackbar('Etapa creada correctamente.', { variant: 'success' });
      setForm(initialForm);
      setCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-stages'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ stageId, payload }: { stageId: string; payload: CreateStagePayload }) =>
      updateIndustrialCostStage(stageId, payload),
    onSuccess: async () => {
      enqueueSnackbar('Etapa actualizada correctamente.', { variant: 'success' });
      setEditingStage(null);
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-stages'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (stageId: string) => deleteIndustrialCostStage(stageId),
    onSuccess: async () => {
      enqueueSnackbar('Etapa eliminada correctamente.', { variant: 'success' });
      setDeletingStage(null);
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-stages'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async ({
      first,
      second,
    }: {
      first: Stage;
      second: Stage;
    }) => {
      const firstPayload: CreateStagePayload = {
        ...buildStagePayload(first),
        display_order: second.display_order,
      };

      const secondPayload: CreateStagePayload = {
        ...buildStagePayload(second),
        display_order: first.display_order,
      };

      await Promise.all([
        updateIndustrialCostStage(first.id, firstPayload),
        updateIndustrialCostStage(second.id, secondPayload),
      ]);
    },
    onSuccess: async () => {
      enqueueSnackbar('Orden actualizado correctamente.', { variant: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-stages'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const normalizeMutation = useMutation({
    mutationFn: async ({ stageType, items }: { stageType: StageType; items: Stage[] }) => {
      const sorted = sortStages(items).filter((item) => item.stage_type === stageType);

      const updates = sorted
        .map((stage, index) => {
          const nextOrder = index + 1;
          if (stage.display_order === nextOrder) {
            return null;
          }

          return updateIndustrialCostStage(stage.id, {
            ...buildStagePayload(stage),
            display_order: nextOrder,
          });
        })
        .filter(Boolean) as Promise<void>[];

      if (updates.length === 0) {
        return 'noop';
      }

      await Promise.all(updates);
      return 'updated';
    },
    onSuccess: async (result, variables) => {
      if (result === 'noop') {
        enqueueSnackbar(
          variables.stageType === 'primary'
            ? 'El orden de etapas principales ya estaba normalizado.'
            : 'El orden de etapas auxiliares ya estaba normalizado.',
          { variant: 'info' },
        );
        return;
      }

      enqueueSnackbar(
        variables.stageType === 'primary'
          ? 'Orden de etapas principales normalizado.'
          : 'Orden de etapas auxiliares normalizado.',
        { variant: 'success' },
      );
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-stages'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const counts = useMemo(() => {
    const primary = stages.filter((item) => item.stage_type === 'primary').length;
    const auxiliary = stages.filter((item) => item.stage_type === 'auxiliary').length;
    const active = stages.filter((item) => item.is_active).length;

    return { primary, auxiliary, active, total: stages.length };
  }, [stages]);

  const primaryStages = useMemo(
    () => sortStages(stages.filter((item) => item.stage_type === 'primary')),
    [stages],
  );

  const auxiliaryStages = useMemo(
    () => sortStages(stages.filter((item) => item.stage_type === 'auxiliary')),
    [stages],
  );

  const handleChange = <K extends keyof CreateStagePayload>(
    key: K,
    value: CreateStagePayload[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditChange = <K extends keyof CreateStagePayload>(
    key: K,
    value: CreateStagePayload[K],
  ) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    createMutation.mutate({
      ...form,
      name: form.name.trim(),
      code: form.code.trim(),
      description: form.description?.trim() || null,
    });
  };

  const handleOpenEdit = (stage: Stage) => {
    setEditingStage(stage);
    setEditForm({
      name: stage.name,
      code: stage.code,
      stage_type: stage.stage_type,
      display_order: stage.display_order,
      is_active: stage.is_active,
      description: stage.description || '',
    });
  };

  const handleConfirmEdit = () => {
    if (!editingStage) return;

    updateMutation.mutate({
      stageId: editingStage.id,
      payload: {
        ...editForm,
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        description: editForm.description?.trim() || null,
      },
    });
  };

  const handleConfirmDelete = () => {
    if (!deletingStage) return;
    deleteMutation.mutate(deletingStage.id);
  };

  const handleMoveStage = (stage: Stage, direction: 'up' | 'down') => {
    const group = stage.stage_type === 'primary' ? primaryStages : auxiliaryStages;
    const index = group.findIndex((item) => item.id === stage.id);

    if (index === -1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= group.length) return;

    reorderMutation.mutate({
      first: group[index],
      second: group[targetIndex],
    });
  };

  const canSubmit =
    form.name.trim().length > 0 &&
    form.code.trim().length > 0 &&
    Number.isFinite(form.display_order);

  const canEditSubmit =
    editForm.name.trim().length > 0 &&
    editForm.code.trim().length > 0 &&
    Number.isFinite(editForm.display_order);

  const renderStageTable = (stageList: Stage[]) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          {['#', 'Nombre', 'Código', 'Estado', 'Orden', 'Acciones'].map((h) => (
            <TableCell
              key={h}
              sx={{
                color: '#94a3b8',
                fontWeight: 800,
                fontSize: '0.72rem',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                borderBottom: '1px solid rgba(148,163,184,0.12)',
                py: 1,
              }}
            >
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {stageList.map((stage, index) => (
          <TableRow
            key={stage.id}
            sx={{
              '&:hover': { bgcolor: 'rgba(148,163,184,0.04)' },
              '& td': { borderBottom: '1px solid rgba(148,163,184,0.08)', py: 0, height: 52 },
            }}
          >
            <TableCell sx={{ color: '#38bdf8', fontWeight: 800, width: 40 }}>
              {index + 1}
            </TableCell>
            <TableCell>
              <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '1rem' }}>
                {stage.name}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600 }}>
                {stage.code}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={stage.is_active ? 'Activa' : 'Inactiva'}
                size="small"
                sx={{
                  bgcolor: stage.is_active ? 'rgba(34,197,94,0.10)' : 'rgba(148,163,184,0.10)',
                  color: stage.is_active ? '#22c55e' : '#94a3b8',
                  border: `1px solid ${stage.is_active ? 'rgba(34,197,94,0.24)' : 'rgba(148,163,184,0.20)'}`,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                }}
              />
            </TableCell>
            <TableCell sx={{ width: 80 }}>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleMoveStage(stage, 'up')}
                  disabled={index === 0 || reorderMutation.isPending}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0.25,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderColor: 'rgba(148,163,184,0.28)',
                    color: '#cbd5e1',
                  }}
                >
                  ↑
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleMoveStage(stage, 'down')}
                  disabled={index === stageList.length - 1 || reorderMutation.isPending}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0.25,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderColor: 'rgba(148,163,184,0.28)',
                    color: '#cbd5e1',
                  }}
                >
                  ↓
                </Button>
              </Stack>
            </TableCell>
            <TableCell sx={{ width: 140 }}>
              <Stack direction="row" spacing={0.75}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenEdit(stage)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    borderColor: 'rgba(56,189,248,0.28)',
                    color: '#38bdf8',
                    py: 0.3,
                  }}
                >
                  Editar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setDeletingStage(stage)}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', py: 0.3 }}
                >
                  Eliminar
                </Button>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack spacing={1.25}>
              <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Etapas
              </Typography>

              <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
                La estructura productiva debe verse con claridad. Por eso esta vista da
                protagonismo a las etapas y separa principales y auxiliares.
              </Typography>

              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
                El orden operativo se gestiona desde aquí, sin dejar el formulario ocupando
                espacio constante en pantalla.
              </Typography>
            </Stack>

            <Button
              variant="contained"
              onClick={() => setCreateOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 2.25,
                py: 1.15,
                bgcolor: '#38bdf8',
                color: '#0f172a',
                '&:hover': {
                  bgcolor: '#38bdf8',
                  filter: 'brightness(1.06)',
                },
              }}
            >
              Nueva etapa
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          {[
            { label: 'Total', value: counts.total },
            { label: 'Primary', value: counts.primary },
            { label: 'Auxiliary', value: counts.auxiliary },
            { label: 'Activas', value: counts.active },
          ].map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.58)',
                px: 2,
                py: 1.75,
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {item.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.4 }}
              >
                {item.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {isError ? (
          <Alert severity="error">{getErrorMessage(error)}</Alert>
        ) : isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              px: 3,
              py: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Cargando etapas...
            </Typography>
          </Paper>
        ) : stages.length === 0 ? (
          <IndustrialCostSectionPlaceholder
            title="Sin etapas todavía"
            description="Crea las primeras etapas para empezar a modelar la estructura productiva de la planta."
            note="Más adelante estas etapas serán la base de los repartos y del cálculo final."
          />
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.66)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 3,
                }}
              >
                <Stack spacing={2.25}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        Etapas principales
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        Son las etapas que recibirán finalmente los costes tras los repartos.
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      onClick={() =>
                        normalizeMutation.mutate({
                          stageType: 'primary',
                          items: primaryStages,
                        })
                      }
                      disabled={normalizeMutation.isPending}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: 'rgba(56,189,248,0.28)',
                        color: '#38bdf8',
                      }}
                    >
                      Normalizar orden
                    </Button>
                  </Stack>

                  {primaryStages.length === 0 ? (
                    <IndustrialCostSectionPlaceholder
                      title="Sin etapas principales"
                      description="Crea al menos una etapa principal para construir la estructura productiva principal."
                    />
                  ) : (
                    renderStageTable(primaryStages)
                  )}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.66)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 3,
                }}
              >
                <Stack spacing={2.25}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        Etapas auxiliares
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        Son etapas de soporte que luego se reparten hacia las principales.
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      onClick={() =>
                        normalizeMutation.mutate({
                          stageType: 'auxiliary',
                          items: auxiliaryStages,
                        })
                      }
                      disabled={normalizeMutation.isPending}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: 'rgba(245,158,11,0.28)',
                        color: '#f59e0b',
                      }}
                    >
                      Normalizar orden
                    </Button>
                  </Stack>

                  {auxiliaryStages.length === 0 ? (
                    <IndustrialCostSectionPlaceholder
                      title="Sin etapas auxiliares"
                      description="Añade etapas auxiliares cuando existan tareas de soporte como mantenimiento o limpieza."
                    />
                  ) : (
                    renderStageTable(auxiliaryStages)
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Stack>

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nueva etapa</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Código"
              value={form.code}
              onChange={(e) => handleChange('code', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              select
              label="Tipo de etapa"
              value={form.stage_type}
              onChange={(e) => handleChange('stage_type', e.target.value as StageType)}
              fullWidth
              size="small"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="auxiliary">Auxiliary</MenuItem>
            </TextField>

            <TextField
              label="Orden"
              type="number"
              value={form.display_order}
              onChange={(e) => handleChange('display_order', Number(e.target.value))}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 3,
                bgcolor: 'rgba(30,41,59,0.06)',
                border: '1px solid rgba(148,163,184,0.18)',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Activa
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Solo las etapas activas deberían admitirse para nuevas altas.
                </Typography>
              </Box>

              <Switch
                checked={form.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
            </Stack>

            <TextField
              label="Descripción"
              value={form.description ?? ''}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || createMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {createMutation.isPending ? 'Guardando...' : 'Crear etapa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingStage}
        onClose={() => !updateMutation.isPending && setEditingStage(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar etapa</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              value={editForm.name}
              onChange={(e) => handleEditChange('name', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Código"
              value={editForm.code}
              onChange={(e) => handleEditChange('code', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              select
              label="Tipo de etapa"
              value={editForm.stage_type}
              onChange={(e) => handleEditChange('stage_type', e.target.value as StageType)}
              fullWidth
              size="small"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="auxiliary">Auxiliary</MenuItem>
            </TextField>

            <TextField
              label="Orden"
              type="number"
              value={editForm.display_order}
              onChange={(e) => handleEditChange('display_order', Number(e.target.value))}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 3,
                bgcolor: 'rgba(30,41,59,0.06)',
                border: '1px solid rgba(148,163,184,0.18)',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Activa
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Cambia su disponibilidad operativa.
                </Typography>
              </Box>

              <Switch
                checked={editForm.is_active}
                onChange={(e) => handleEditChange('is_active', e.target.checked)}
              />
            </Stack>

            <TextField
              label="Descripción"
              value={editForm.description ?? ''}
              onChange={(e) => handleEditChange('description', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setEditingStage(null)}
            disabled={updateMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmEdit}
            disabled={!canEditSubmit || updateMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {updateMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deletingStage}
        onClose={() => !deleteMutation.isPending && setDeletingStage(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar etapa</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            {deletingStage
              ? `Vas a eliminar la etapa "${deletingStage.name}". Esta acción no se puede deshacer.`
              : ''}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeletingStage(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}